import { useEffect, useMemo } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { STABLE_EMPTY_OBJECT } from '../../common/utils';
import AtlassianSystemRemoteFrontendSdk from '../remote-sdk';
import useRemoteModuleLoader from '../use-remote-module-loader';
const preloadedModuleMap = new WeakMap();

/**
 * Create an SDK instance for a given plugin aand cache it in the WeakMap so that if a plugin is no longer referenced
 * we clean up the sdk from the cache
 *
 * @param remotePlugin
 */
const cacheAndGetSdkForPlugin = remotePlugin => {
  const cachedSdk = preloadedModuleMap.get(remotePlugin);
  if (cachedSdk) {
    return cachedSdk;
  }
  const sdk = new AtlassianSystemRemoteFrontendSdk(remotePlugin);
  preloadedModuleMap.set(remotePlugin, sdk);
  return sdk;
};

/**
 * Preload a plugin by instantiating it and firing off the request for the module in the registry
 *
 * @param remotePlugin
 * @param options
 */
const useRemoteModule = (remotePlugin, options = STABLE_EMPTY_OBJECT) => {
  const remoteFrontendSdk = cacheAndGetSdkForPlugin(remotePlugin);
  const experience = remoteFrontendSdk.renderUfoExperience;
  const moduleName = remoteFrontendSdk.getModuleName();
  useEffect(() => {
    if (experience.state.id === 'NOT_STARTED') {
      experience.start();
    }
  }, [experience]);
  const {
    createAnalyticsEvent
  } = useAnalyticsEvents();
  useEffect(() => {
    const analyticsEvent = createAnalyticsEvent({
      action: 'rendered',
      actionSubject: 'remoteModule',
      actionSubjectId: 'useRemoteModule',
      attributes: {
        dimensions: remotePlugin.dimensions,
        plugin: remotePlugin.constructor.name
      }
    });
    analyticsEvent.fire(options === null || options === void 0 ? void 0 : options.analyticsChannel);
  }, [createAnalyticsEvent, remotePlugin, options.analyticsChannel]);
  const optionsWithError = useMemo(() => {
    const onError = error => {
      var _options$onError;
      void experience.failure({
        metadata: {
          moduleName
        }
      });
      options === null || options === void 0 ? void 0 : (_options$onError = options.onError) === null || _options$onError === void 0 ? void 0 : _options$onError.call(options, error);
    };
    return {
      ...options,
      onError
    };
  }, [experience, moduleName, options]);
  useRemoteModuleLoader(remoteFrontendSdk, optionsWithError);
  return remoteFrontendSdk;
};
export default useRemoteModule;